import { memo } from 'react';
import { Button, Popconfirm, Dropdown, Checkbox, Space } from 'antd';
import { DeleteOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

interface BulkActionsBarProps {
  selectedCount: number;
  totalCount: number;
  onBulkDelete: () => void;
  onDownload: (format: 'xlsx' | 'geojson' | 'csv' | 'shp') => void;
  onClearSelection: () => void;
  onSelectAll: () => void;
  loading?: boolean;
  showDownload?: boolean;
}

const BulkActionsBar = ({
  selectedCount,
  totalCount,
  onBulkDelete,
  onDownload,
  onClearSelection,
  onSelectAll,
  loading,
  showDownload = true,
}: BulkActionsBarProps) => {
  if (selectedCount === 0) return null;

  const isAllSelected = selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  const downloadMenuItems = [
    {
      key: 'xlsx',
      label: 'Excel (.xlsx)',
      onClick: () => onDownload('xlsx'),
    },
    {
      key: 'geojson',
      label: 'GeoJSON (.geojson)',
      onClick: () => onDownload('geojson'),
    },
    {
      key: 'csv',
      label: 'CSV (.csv)',
      onClick: () => onDownload('csv'),
    },
    {
      key: 'shp',
      label: 'Shapefile (.zip)',
      onClick: () => onDownload('shp'),
    },
  ];

  return (
    <Container>
      <LeftSection>
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => {
            if (e.target.checked) {
              onSelectAll();
            } else {
              onClearSelection();
            }
          }}
        />
        <SelectedCount>
          {selectedCount} bản ghi đã chọn
          {!isAllSelected && totalCount > 0 && ` / ${totalCount}`}
        </SelectedCount>
      </LeftSection>

      <RightSection>
        <Popconfirm
          title="Xác nhận xóa"
          description={`Bạn có chắc muốn xóa ${selectedCount} bản ghi đã chọn? Hành động này không thể hoàn tác.`}
          onConfirm={onBulkDelete}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true, loading }}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={loading}
          >
            Xóa đã chọn
          </Button>
        </Popconfirm>

        {showDownload && (
          <Dropdown menu={{ items: downloadMenuItems }} placement="bottomRight">
            <Button size="small" icon={<DownloadOutlined />}>
              Tải xuống
            </Button>
          </Dropdown>
        )}

        <TooltipButton
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClearSelection}
          title="Bỏ chọn tất cả"
        />
      </RightSection>
    </Container>
  );
};

export default memo(BulkActionsBar);

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border: 1px solid #7dd3fc;
  border-radius: 6px;
  margin-bottom: 6px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectedCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #0369a1;
`;

const TooltipButton = styled(Button)`
  color: #0369a1;
  
  &:hover {
    color: #0c4a6e;
    background: rgba(14, 165, 233, 0.1);
  }
`;
